/**
 * Onglet « Photos à la dépose » d'un OR (extrait de OrDetail.jsx — iter31).
 * 4 coins véhicule + jauge carburant + tableau de bord km. Photos compressées (~500 KB JPEG).
 * iter239 — chaque photo peut être marquée « partagée » : elle apparaît alors
 * sur le lien de suivi public du client (si le lien autorise les photos).
 */
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, Eye, EyeOff, X, ImageIcon } from "lucide-react";

export default function OrPhotosTab({ photos, partagees = [], onUpload, onRemove, onTogglePartage, onOpenLibrary }) {
  return (
    <Card className="p-5 border-gray-200">
      <h3 className="font-heading font-semibold mb-2">Photos à la dépose</h3>
      <p className="text-sm text-gray-500 mb-4">
        4 coins du véhicule + jauge carburant + tableau de bord km.
        Obligatoire pour la conformité.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {(photos || []).map((p, i) => {
          const shared = partagees.includes(i);
          return (
            <div key={i} className="relative group" data-testid={`photo-${i}`}>
              <img
                src={p}
                alt={`Photo ${i + 1}`}
                className={`w-full h-32 object-cover rounded-md border-2 ${shared ? "border-[#52a675]" : "border-gray-200"}`}
              />
              <button
                onClick={() => onRemove(i)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`photo-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
              <button
                onClick={() => onTogglePartage?.(i, !shared)}
                className={`absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 rounded text-[10px] py-1 transition-colors ${
                  shared ? "bg-[#52a675] text-white" : "bg-black/50 text-white opacity-0 group-hover:opacity-100"
                }`}
                title={shared ? "Visible par le client — cliquer pour retirer" : "Partager cette photo avec le client"}
                data-testid={`photo-partage-${i}`}
              >
                {shared ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                {shared ? "Partagée" : "Partager"}
              </button>
            </div>
          );
        })}
        <label
          className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col items-center justify-center cursor-pointer hover:border-[#52a675] hover:bg-[#f0f9f4] h-32"
          data-testid="photo-upload-label"
        >
          <Camera className="w-6 h-6 text-gray-400 mb-1" />
          <span className="text-xs text-gray-500">Ajouter photos</span>
          <input
            type="file"
            multiple
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => onUpload(e.target.files)}
            data-testid="photo-upload-input"
          />
        </label>
      </div>
      {onOpenLibrary && (
        <Button
          variant="outline" size="sm" className="mb-3 border-gray-300"
          onClick={onOpenLibrary}
          data-testid="or-photos-library-btn"
        >
          <ImageIcon className="w-4 h-4 mr-2 text-[#52a675]" /> Choisir dans la bibliothèque
        </Button>
      )}
      <p className="text-xs text-gray-400">
        Les photos sont compressées (max 1280×1280, ~500 KB JPEG). Les photos entourées de vert
        sont visibles par le client sur son lien de suivi.
      </p>
    </Card>
  );
}
